// ============================================================
// PETFLYCHECK SEO DATA - MERGED (Complete Dataset)
// Combines seoData.js and seoDataExtended.js for 80+ pages
// ============================================================

import { airlinesData, countriesData, breedsData, guidesData } from './seoData';
import { additionalAirlinesData, additionalCountriesData, additionalBreedsData, additionalGuidesData } from './seoDataExtended';

const fit = (text, max) => (text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`);

const META_OVERRIDES = {
  'iata-crate-size-calculator': ['IATA Crate Size Calculator for Dogs & Cats — Free Tool', 'Work out the minimum crate size your dog or cat needs to fly, using the official IATA formula. Free, instant, in cm or inches. Includes how to measure.'],
  'iata-crate-requirements-2026': ['IATA Crate Requirements 2026: The Full Checklist', 'Size formula, bolts, ventilation, door, bowls, labels, wheels and floor: every IATA pet crate rule explained in plain English, with a free calculator.'],
  'metal-bolts-vs-plastic-clips': ['Metal Bolts vs Plastic Clips: What IATA Requires', "IATA says plastic clips can't be the only fasteners on a pet crate. What that means, how to check your crate, and how to fit metal bolts."],
  'labrador-retriever': ['Labrador Crate Size for Flying: IATA Guide', 'How to work out the IATA crate size for your Labrador, step by step: what to measure, why inside dimensions matter, and what else the crate needs.'],
  'shih-tzu': ['Shih Tzu Crate Size for Flying: IATA +10% Rule', 'Shih Tzus are a snub-nosed breed, so IATA asks for a 10% larger crate. How to measure, the formula, and what airlines check. Free calculator.'],
  'south-korea': ['Flying a Dog or Cat to South Korea: Crate & Entry Guide', 'Bringing your dog or cat to South Korea: the IATA crate rules, the entry requirements to check, and a document checklist before you book.'],
  klm: ['KLM Pet Crate Rules: IATA Size & Checklist', 'Flying with your pet on KLM? Check your crate against IATA size and container rules, plus what to confirm with KLM before you fly.'],
  lufthansa: ['Lufthansa Pet Crate Rules: IATA Size & Checklist', 'Flying with your pet on Lufthansa? Check your crate against IATA size and container rules, plus what to confirm with Lufthansa before you fly.'],
  'singapore-airlines': ['Singapore Airlines Pet Crate: IATA Size & Checklist', 'Flying your pet with Singapore Airlines? Check your crate against IATA size and container rules, plus what to confirm with the airline first.'],
};

const withMeta = (data, makeTitle, makeDesc) => Object.fromEntries(Object.entries(data).map(([slug, item]) => {
  const [title, desc] = META_OVERRIDES[slug] || [makeTitle(item), makeDesc(item)];
  return [slug, { ...item, seoTitle: fit(title, 60), seoDescription: fit(desc, 155) }];
}));

// Merge all data sources
export const allAirlinesData = withMeta({
  ...airlinesData,
  ...additionalAirlinesData
}, (a) => `${a.name} Pet Crate Rules: IATA Size & Checklist`,
   (a) => `Flying with your pet on ${a.name}? Check your crate against IATA size and container rules, plus what to confirm with ${a.name} before you fly.`);

export const allCountriesData = withMeta({
  ...countriesData,
  ...additionalCountriesData
}, (c) => c.seoTitle || `Flying a Pet to ${c.name}: Crate & Entry Guide`, (c) => c.seoDescription || `Bringing your dog or cat to ${c.name}: IATA crate rules and the entry requirements to check before you book.`);

export const allBreedsData = withMeta({
  ...breedsData,
  ...additionalBreedsData
}, (b) => `${b.name} Crate Size for Flying: IATA Guide`,
   (b) => `How to work out the IATA crate size for your ${b.name}: what to measure, the official formula, and what else the crate needs. Free calculator.`);

export const allGuidesData = withMeta({
  ...guidesData,
  ...additionalGuidesData
}, (g) => g.seoTitle || g.title, (g) => g.seoDescription || g.introduction);

// Export counts for sitemap generation
export const counts = {
  airlines: Object.keys(allAirlinesData).length,
  countries: Object.keys(allCountriesData).length,
  breeds: Object.keys(allBreedsData).length,
  guides: Object.keys(allGuidesData).length,
  total: Object.keys(allAirlinesData).length + 
         Object.keys(allCountriesData).length + 
         Object.keys(allBreedsData).length + 
         Object.keys(allGuidesData).length + 
         4 // Hub pages
};

export default {
  allAirlinesData,
  allCountriesData,
  allBreedsData,
  allGuidesData,
  counts
};
